import * as React from "react"

import { cn } from "@/lib/utils"

// Chromium (Chrome/Edge) snapshots a form's values on every keystroke and, when the form later
// disappears (dialog closed, route change, XHR), offers to save what it saw — its AI classifier
// happily calls a 5‑digit ZIP a "driver's license" and ignores autocomplete hints. Two defenses:
// 1. Numeric fields are detached from their <form> (form="…" pointing nowhere) so a native submit
//    event never carries their value; Enter still submits via requestSubmit().
// 2. Opted-out fields clear their DOM value right before unmount. Chromium mirrors JS value changes
//    into its snapshot, so the "submitted" form it inspects has nothing to save.
const NO_AUTOFILL = "v2r-no-autofill";
const DETACHED_FORM_ID = "v2r-detached-form";

const Input = React.forwardRef(({ className, type, inputMode, autoComplete, form, onKeyDown, ...props }, ref) => {
  // Auto-set inputMode for number inputs to show numeric keyboard on mobile
  const computedInputMode = inputMode || (type === "number" ? "decimal" : undefined);
  const computedAutoComplete = autoComplete == null || autoComplete === "off" ? (type === "password" ? "current-password" : NO_AUTOFILL) : autoComplete;

  const scrub = computedAutoComplete === NO_AUTOFILL && type !== "file";
  const detach = scrub && form == null && (type === "number" || computedInputMode === "numeric" || computedInputMode === "decimal");

  const innerRef = React.useRef(null);
  const setRefs = React.useCallback((node) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  React.useLayoutEffect(() => {
    if (!scrub) return undefined;
    const mounted = innerRef.current;
    // StrictMode (dev) runs cleanup+setup once on mount; restore what that fake unmount cleared.
    if (mounted && mounted.__v2rScrubbed != null) {
      mounted.value = mounted.__v2rScrubbed;
      mounted.__v2rScrubbed = null;
    }
    return () => {
      const el = innerRef.current;
      if (el && el.isConnected && el.value) {
        el.__v2rScrubbed = el.value;
        el.value = "";
      }
    };
  }, [scrub]);

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
    if (detach && e.key === "Enter" && !e.defaultPrevented) {
      const owner = e.currentTarget.closest("form");
      if (owner) {
        e.preventDefault();
        owner.requestSubmit();
      }
    }
  };

  return (
    <input
      type={type}
      inputMode={computedInputMode}
      autoComplete={computedAutoComplete}
      form={detach ? DETACHED_FORM_ID : form}
      onKeyDown={handleKeyDown}
      data-lpignore="true"
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={setRefs}
      {...props} />
  );
})
Input.displayName = "Input"

// Full-page navigations (window.location, reload) make Chromium inspect its last snapshot too.
if (typeof window !== "undefined" && !window.__v2rScrubBound) {
  window.__v2rScrubBound = true;
  window.addEventListener("beforeunload", () => {
    document.querySelectorAll(`input[autocomplete="${NO_AUTOFILL}"]`).forEach((el) => { if (el.value) el.value = ""; });
  });
}

export { Input }
